/**
 * Alert Component
 *
 * Contextual feedback messages for user actions.
 * Replaces inline error/success/warning banners throughout the app.
 *
 * Features:
 * - 4 semantic variants (info, success, warning, error)
 * - Optional title and close button
 * - Custom icons supported
 * - Full dark mode support
 * - Accessible with ARIA attributes
 *
 * Usage:
 *   <Alert variant="error" title="Error" onClose={() => setError(null)}>
 *     Failed to save changes. Please try again.
 *   </Alert>
 */

import React from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AlertProps {
  /** Visual variant of the alert */
  variant?: 'info' | 'success' | 'warning' | 'error';
  /** Optional title text */
  title?: string;
  /** Alert content */
  children: React.ReactNode;
  /** Callback when close button is clicked */
  onClose?: () => void;
  /** Custom icon (overrides default variant icon) */
  icon?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  title,
  children,
  onClose,
  icon,
  className
}) => {
  const variants = {
    info: {
      container: 'bg-work-soft',
      icon: 'text-work',
      title: 'text-ink',
      text: 'text-ink',
      defaultIcon: <Info className="w-5 h-5" />
    },
    success: {
      container: 'bg-ok-soft',
      icon: 'text-ok',
      title: 'text-ink',
      text: 'text-ink',
      defaultIcon: <CheckCircle className="w-5 h-5" />
    },
    warning: {
      container: 'bg-warn-soft',
      icon: 'text-warn',
      title: 'text-ink',
      text: 'text-ink',
      defaultIcon: <AlertTriangle className="w-5 h-5" />
    },
    error: {
      container: 'bg-bad-soft',
      icon: 'text-bad',
      title: 'text-ink',
      text: 'text-ink',
      defaultIcon: <AlertCircle className="w-5 h-5" />
    }
  };

  const variantStyles = variants[variant];

  return (
    <div
      className={cn(
        'rounded-[14px] px-4 py-3 flex gap-3 text-sm leading-normal',
        variantStyles.container,
        className
      )}
      role="alert"
    >
      {/* Icon */}
      <div className={cn('flex-shrink-0', variantStyles.icon)}>
        {icon || variantStyles.defaultIcon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <h3 className={cn('font-semibold mb-1', variantStyles.title)}>
            {title}
          </h3>
        )}
        <div className={cn('text-sm text-ink', variantStyles.text)}>
          {children}
        </div>
      </div>

      {/* Close button */}
      {onClose && (
        <button
          onClick={onClose}
          className={cn(
            'flex-shrink-0',
            'text-ink-3 hover:text-ink-2',
            'transition-colors'
          )}
          aria-label="Close alert"
        >
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default Alert;
